/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AuthorizationCodesFormView.tsx (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Authorization Codes.
    Binds codes to Class of Service with optional alias for CDR privacy.

 🔧 Notes:
    - Refactored from monolithic 622-line file
    - Logic delegated to hooks/
    - API calls moved to api/
    - Class of Services loading kept in component for now

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shield, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { ModuleFormHeader } from '../../components/layouts';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { Label } from '../../components/ui/label';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import SelectClassOfService from '../../components/ui/SelectClassOfService';
import { useContextStore } from '../../store/context';
import { deleteAuthorizationCodeById, navigateToAuthorizationCodeDuplicate } from './authorizationCodesActions';
import { useAuthorizationCodeData } from './hooks/useAuthorizationCodeData';
import { useAuthorizationCodeForm } from './hooks/useAuthorizationCodeForm';
import { fetchAuthorizationCodes } from './api';
import type { AuthorizationCode } from './types';

interface QuickListItem {
  id: number;
  uuid?: string;
  code: string;
  alias?: string | null;
  description?: string | null;
  active: boolean;
}

export default function AuthorizationCodesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const isDuplicateMode = Boolean(duplicateId);

  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError } = useNotifications();

  const { selectedTenant, selectedDomain } = useContextStore();
  const tenantSlug = selectedTenant?.slug ?? 'main';

  const { formData: initialData, isLoading } = useAuthorizationCodeData(id, duplicateId, tenantSlug);

  const {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSave
  } = useAuthorizationCodeForm(id, duplicateId, tenantSlug, initialData);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return formData;
    }
    return savedData;
  }, [handleSave, formData]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-service/auth-codes',
    newPath: '/pbx/class-of-service/auth-codes/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-service/auth-codes/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => formData,
    storageKey: 'authorizationCodes:saveAndNewData',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [classOfServices, setClassOfServices] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingCos, setLoadingCos] = useState(false);
  const defaultCosAppliedRef = useRef(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<QuickListItem[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Load Class of Services
  useEffect(() => {
    const domainId = selectedDomain?.id ?? null;

    if (!domainId) {
      setClassOfServices([]);
      setLoadingCos(false);
      return;
    }

    const params = new URLSearchParams({ limit: '500', domainId: String(domainId) });
    const controller = new AbortController();
    let cancelled = false;

    const fetchCos = async () => {
      setLoadingCos(true);
      try {
        const response = await fetch(
          buildApiUrl(`/api/telephony/class-of-services?${params.toString()}`),
          {
            headers: getApiHeaders(tenantSlug),
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error();
        }

        const result = await response.json();
        if (cancelled) {
          return;
        }

        const items = Array.isArray(result.data) ? result.data : [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const normalized = items.map((cos: any) => ({
          id: cos.id,
          name: cos.name,
          description: cos.description ?? undefined,
          isMain: cos.isMain ?? cos.is_main ?? false
        }));

        setClassOfServices(normalized);

        if (!isEditMode && !isDuplicateMode && normalized.length && !defaultCosAppliedRef.current) {
          const defaultCos =
            normalized.length === 1
              ? normalized[0]
              : normalized.find((cos) => cos.isMain) ?? null;

          if (defaultCos) {
            handleChange('cosId', defaultCos.id);
            defaultCosAppliedRef.current = true;
          }
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        showError(t('authorizationCodes.messages.loadCosError'));
        setClassOfServices([]);
      } finally {
        if (!cancelled) {
          setLoadingCos(false);
        }
      }
    };

    fetchCos();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isDuplicateMode, isEditMode, selectedDomain?.id, tenantSlug, handleChange, showError, t]);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAuthorizationCodeById(id, tenantSlug);

      showSuccess(t('authorizationCodes.messages.deleteSuccess'));
      navigate('/pbx/class-of-service/auth-codes');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('authorizationCodes.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, navigate, showError, showSuccess, t, tenantSlug]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToAuthorizationCodeDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/class-of-service/auth-codes/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    const domainId = selectedDomain?.id;
    if (!domainId) {
      showError(t('authorizationCodes.messages.domainMissing'));
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchAuthorizationCodes({
        page: 1,
        limit: 100,
        sortBy: 'code',
        sortOrder: 'asc',
        domainId
      }, tenantSlug);

      const items = Array.isArray(result?.data) ? (result.data as QuickListItem[]) : [];
      setQuickListItems(items);
    } catch {
      showError(t('authorizationCodes.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [selectedDomain?.id, showError, t, tenantSlug]);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) => {
      const alias = item.alias ?? '';
      const description = item.description ?? '';
      return (
        item.code.toLowerCase().includes(query) ||
        alias.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      );
    });
  }, [quickListItems, quickListSearch]);

  const pageTitle = !isEditMode || isDuplicateMode
    ? t('authorizationCodes.form.titleNew')
    : `${t('authorizationCodes.form.titleEdit')} - ${formData?.code || ''}`;

  // Show loading state only when editing and data hasn't loaded yet
  if (isLoading && !initialData && isEditMode && !isDuplicateMode) {
    return (
      <div className="p-6 pb-24">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // For new entries or when data is available, show the form
  return (
    <div className="p-6 pb-24"><ModuleFormHeader
        icon={<Shield className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('authorizationCodes.actions.list')}
        onBack={() => navigate('/pbx/class-of-service/auth-codes')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('authorizationCodes.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('authorizationCodes.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('authorizationCodes.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <form className="mt-8 space-y-6">
        <FormBox title={t('authorizationCodes.title')} isLoading={isLoading}>
          {!selectedDomain && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {t('authorizationCodes.messages.domainMissing')}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            <Label htmlFor="auth-code-code" className="text-sm font-medium text-muted-foreground">
              {t('authorizationCodes.fields.code.label')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <div>
              <Input
                id="auth-code-code"
                value={formData?.code ?? ''}
                placeholder={t('authorizationCodes.fields.code.placeholder')}
                onChange={(event) => handleChange('code', event.target.value)}
                className={errors.code ? 'border-destructive' : undefined}
              />
              {errors.code && (
                <p className="mt-1 text-xs text-destructive">{errors.code}</p>
              )}
            </div>

            <Label htmlFor="auth-code-alias" className="text-sm font-medium text-muted-foreground">
              {t('authorizationCodes.fields.alias.label')}
            </Label>
            <div>
              <Input
                id="auth-code-alias"
                value={formData?.alias ?? ''}
                placeholder={t('authorizationCodes.fields.alias.placeholder')}
                onChange={(event) => handleChange('alias', event.target.value)}
              />
            </div>

            <Label htmlFor="auth-code-description" className="text-sm font-medium text-muted-foreground">
              {t('authorizationCodes.fields.description.label')}
            </Label>
            <div>
              <Input
                id="auth-code-description"
                value={formData?.description ?? ''}
                placeholder={t('authorizationCodes.fields.description.placeholder')}
                onChange={(event) => handleChange('description', event.target.value)}
              />
            </div>

            <Label htmlFor="auth-code-cos" className="text-sm font-medium text-muted-foreground">
              {t('authorizationCodes.fields.classOfService.label')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <div>
              <SelectClassOfService
                value={formData?.cosId ?? null}
                onChange={(value) => handleChange('cosId', value !== null ? Number(value) : null)}
                classOfServices={classOfServices}
                placeholder={t('authorizationCodes.fields.classOfService.placeholder')}
                disabled={loadingCos}
              />
              {errors.cosId && (
                <p className="mt-1 text-xs text-destructive">{errors.cosId}</p>
              )}
            </div>

            <Label htmlFor="auth-code-active" className="text-sm font-medium text-muted-foreground">
              {t('authorizationCodes.fields.active')}
            </Label>
            <div>
              <Toggle
                value={formData?.active ?? true}
                onChange={(value) => handleChange('active', value)}
              />
            </div>
          </div>
        </FormBox>

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={() => {
            if (!isEditMode) {
              setFormData({ code: '', alias: '', description: '', active: true, cosId: null });
            } else if (originalData) {
              setFormData({...originalData});
            }
          }}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isSaving={isSaving}
          isEditMode={isEditMode}
          useSaveDropdown={true}
          saveText={t('common.save')}
          cancelText={t('common.cancel')}
          deleteText={t('common.delete')}
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('authorizationCodes.messages.deleteConfirm')}
        message={t('authorizationCodes.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('authorizationCodes.quickList.title', 'Authorization Codes')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/class-of-service/auth-codes/${item.uuid || item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/class-of-service/auth-codes/${item.uuid || item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.code}
            </div>
            {(item.alias || item.description) && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.alias || item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('authorizationCodes.quickList.searchPlaceholder', 'Search by code, alias or description')}
        emptyMessage={t('authorizationCodes.quickList.empty', 'No authorization codes found')}
        headerIcon={<Shield className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
